import React, { useRef, useState } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';

/**
 * LogoUpload — drop-in widget that lets the admin pick an image from
 * disk, converts it to a `data:image/...;base64,...` URL and hands it
 * back via `onChange`. The base64 string is stored directly in the
 * parent record so we never depend on an external CDN — handy for the
 * invoice PDF generator which can decode the data URL inline.
 *
 * Props:
 *   value     string  current data URL (or empty)
 *   onChange  func    called with the new data URL (or '' when cleared)
 *   maxBytes  number  reject files larger than this (default 500 KB)
 *   testid    string  root data-testid prefix (default 'logo-upload')
 */
const DEFAULT_MAX = 500 * 1024;

export default function LogoUpload({ value, onChange, maxBytes = DEFAULT_MAX, testid = 'logo-upload' }) {
  const fileRef = useRef(null);
  const [err, setErr] = useState('');

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr('');
    if (!file.type.startsWith('image/')) {
      setErr('Please pick an image file (PNG / JPG / WEBP / SVG).');
      try { e.target.value = ''; } catch (_) {}
      return;
    }
    if (file.size > maxBytes) {
      setErr(`File is ${(file.size / 1024).toFixed(0)} KB — max ${(maxBytes / 1024).toFixed(0)} KB. Compress and try again.`);
      try { e.target.value = ''; } catch (_) {}
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange?.(String(reader.result || ''));
      try { e.target.value = ''; } catch (_) {}
    };
    reader.onerror = () => setErr('Could not read the file.');
    reader.readAsDataURL(file);
  };

  const isDataUrl = (s) => typeof s === 'string' && s.startsWith('data:image/');
  const hasLogo = !!value;

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-start gap-3">
        {/* Preview tile */}
        <div
          className="w-24 h-24 flex-shrink-0 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden"
          data-testid={`${testid}-preview`}
        >
          {hasLogo ? (
            <img src={value} alt="logo" className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon className="w-7 h-7 text-slate-400" />
          )}
        </div>
        {/* Controls */}
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid={`${testid}-pick`}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-md flex items-center gap-1.5 transition-all"
            >
              <Upload className="w-3.5 h-3.5" /> {hasLogo ? 'Replace logo' : 'Upload logo'}
            </button>
            {hasLogo && (
              <button
                type="button"
                onClick={() => { setErr(''); onChange?.(''); }}
                data-testid={`${testid}-clear`}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 flex items-center gap-1.5"
              >
                <X className="w-3.5 h-3.5" /> Remove
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPick}
              data-testid={`${testid}-input`}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5 leading-tight">
            PNG, JPG, WEBP or SVG · max {(maxBytes / 1024).toFixed(0)} KB · stored inside the database
            (no external link). Rendered at ~18&nbsp;mm height on the invoice.
          </p>
          {hasLogo && !isDataUrl(value) && (
            <p className="text-[11px] text-amber-700 mt-1">
              Currently using an external URL. Re-upload to store the logo inline.
            </p>
          )}
        </div>
      </div>
      {err && (
        <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2" data-testid={`${testid}-error`}>
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}
    </div>
  );
}
